/**
 * Everything a page has to give back when it is navigated away from.
 *
 * The OpenSilver app did this by overriding `OnNavigatedFrom` and calling `_vm.Stop()` - on six of
 * its twenty-one pages, which is the problem: the other fifteen either had nothing to release or had
 * quietly stopped releasing it. A page here cannot forget, because the router disposes this object
 * whether or not the page's `unmount` remembers to.
 *
 * Four kinds of thing land here, and all four leak in a way that is invisible until it is not:
 *   * intervals - a 4-second presence poll left running for every page ever visited;
 *   * hub subscriptions - a handler holding a dead page's controls, re-rendering nothing;
 *   * in-flight requests - a fetch that resolves after the page is gone and writes to a control
 *     that is no longer in the tree;
 *   * WebwinImages and object URLs - GPU textures and blobs, which the browser will not collect.
 */
export class Disposables {
  #items: Array<() => void> = [];
  #disposed = false;

  /** Runs `fn` when the page unmounts. */
  add(fn: () => void): void {
    if (this.#disposed) {
      fn();
      return;
    }
    this.#items.push(fn);
  }

  /** A repeating timer, cleared on unmount. */
  interval(fn: () => void, ms: number): void {
    const id = setInterval(fn, ms);
    this.add(() => clearInterval(id));
  }

  /** A one-shot timer, cleared on unmount so a late callback cannot touch a dead page. */
  timeout(fn: () => void, ms: number): void {
    const id = setTimeout(fn, ms);
    this.add(() => clearTimeout(id));
  }

  /**
   * An AbortSignal for a request this page owns.
   *
   * Aborting on unmount is what turns "the user navigated away mid-load" from a write to a detached
   * control into a caught AbortError, which `describeError` renders as nothing.
   */
  signal(): AbortSignal {
    const controller = new AbortController();
    this.add(() => controller.abort());
    return controller.signal;
  }

  /** An object URL, revoked on unmount. */
  objectUrl(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.add(() => URL.revokeObjectURL(url));
    return url;
  }

  /**
   * True once the page has gone.
   *
   * The guard an async handler checks before touching a control: `if (dispose.disposed) return;`
   * after every await. Cheaper to read than to thread an AbortSignal through a chain of view logic,
   * and it is the check that stops a slow list load from rendering into a page nobody is looking at.
   */
  get disposed(): boolean {
    return this.#disposed;
  }

  /**
   * Releases everything, newest first.
   *
   * Reverse order because these nest: a subscription registered after a connection should come off
   * before the connection does. Each one is wrapped, so a handler that throws cannot leave the rest
   * of the page's resources held - which is exactly the shape of leak this class exists to prevent.
   */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (let i = this.#items.length - 1; i >= 0; i--) {
      try {
        this.#items[i]!();
      } catch (error) {
        console.error('CRM: a disposable threw while unmounting a page', error);
      }
    }
    this.#items = [];
  }
}
